import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api/client';
import type { Aparelho, CodigoEmitido, Unidade, VinculoDeAparelho } from '../lib/api/types';
import { PageHeader } from '../components/ui/PageHeader';
import { Carregando, Erro, Vazio } from '../components/ui/Estados';
import { emDataHora } from '../lib/format/datetime';

/* ---------------------------------------------------------------- aparelhos (A22) */

/**
 * Os aparelhos do parque, e o código que põe o primeiro deles em operação.
 *
 * ## Por que esta tela não estava nem no inventário
 * As 21 telas do painel foram desenhadas antes de o vínculo de aparelho existir, e ninguém
 * voltou para acrescentar a vigésima segunda. O resultado passou despercebido porque no
 * desenvolvimento o código sempre saiu de um `curl`: **não havia como um cliente instalar o
 * próprio primeiro aparelho.** É o passo zero de qualquer implantação, e ele dependia de nós.
 *
 * ## O código aparece uma vez
 * O banco guarda só o hash. Quem gerou vê o código nesta tela, dita ao telefone, e pronto —
 * não há "mostrar de novo", e a lista de baixo existe para a outra pergunta, que é *"o que eu
 * te passei ainda vale?"*.
 *
 * ## O que a tela mostra do hardware, e por quê
 * `securityLevel` e `verifiedBoot` não são enfeite: eles são o que o aparelho **provou** no
 * vínculo, e decidem o que uma leitura dele vale. Um parque comprado sem olhar isso pode ser
 * um parque inteiro de `TRUSTED_ENVIRONMENT` onde se esperava `STRONGBOX` — e a diferença só
 * apareceria numa auditoria.
 */
export function AparelhosPage() {
  const clienteDeConsulta = useQueryClient();
  const [unidade, setUnidade] = useState('');
  const [emitido, setEmitido] = useState<CodigoEmitido | null>(null);

  const aparelhos = useQuery({
    queryKey: ['aparelhos'],
    queryFn: () => api.get<Aparelho[]>('/console/devices'),
  });
  const vinculos = useQuery({
    queryKey: ['vinculos-de-aparelho'],
    queryFn: () => api.get<VinculoDeAparelho[]>('/console/devices/enrollments'),
  });
  const unidades = useQuery({
    queryKey: ['unidades'],
    queryFn: () => api.get<Unidade[]>('/console/units'),
  });

  const gerar = useMutation({
    mutationFn: () =>
      api.post<CodigoEmitido>('/console/devices/enrollments', { actorId: unidade }),
    onSuccess: (c) => {
      setEmitido(c);
      void clienteDeConsulta.invalidateQueries({ queryKey: ['vinculos-de-aparelho'] });
    },
  });

  // O nome que o operador lê no alto da tela ("DK-04"), único entre os ativos da unidade (ADR-129).
  const [nomeando, setNomeando] = useState<string | null>(null);
  const [nome, setNome] = useState('');
  const nomear = useMutation({
    mutationFn: (deviceId: string) =>
      api.put<Aparelho[]>(`/console/devices/${deviceId}/label`, { label: nome }),
    onSuccess: (lista) => {
      setNomeando(null);
      clienteDeConsulta.setQueryData(['aparelhos'], lista);
    },
  });

  const bloquear = useMutation({
    mutationFn: (deviceId: string) => api.post<void>(`/console/devices/${deviceId}/revoke`, {}),
    onSuccess: () => void clienteDeConsulta.invalidateQueries({ queryKey: ['aparelhos'] }),
  });

  return (
    <>
      <PageHeader
        titulo="Aparelhos"
        subtitulo="Quem pode produzir evento em nome de uma unidade. O vínculo acontece uma vez por aparelho, e a credencial fica no hardware seguro dele."
      />
      <div className="wrap">
        <div className="duas">
          <div className="card tbl">
            {aparelhos.isPending && <Carregando o="os aparelhos" />}
            {aparelhos.isError && <Erro erro={aparelhos.error} />}
            {aparelhos.data?.length === 0 && (
              <Vazio
                titulo="Nenhum aparelho vinculado"
                // Tela vazia que não diz o que fazer é tela que gera chamado.
                detalhe="Gere um código ao lado e dite para quem estiver com o aparelho na mão."
              />
            )}
            {aparelhos.data && aparelhos.data.length > 0 && (
              <table className="t">
                <thead>
                  <tr>
                    <th>Aparelho</th>
                    <th>Unidade</th>
                    <th>Hardware</th>
                    <th>Último contato</th>
                    <th>Situação</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {aparelhos.data.map((a) => (
                    <tr key={a.id}>
                      <td>
                        {nomeando === a.id ? (
                          <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <input
                              value={nome}
                              maxLength={20}
                              placeholder="DK-04"
                              onChange={(e) => setNome(e.target.value)}
                              style={{ width: 90 }}
                            />
                            <button
                              className="btn btn-primary"
                              disabled={nomear.isPending}
                              onClick={() => nomear.mutate(a.id)}
                            >
                              Salvar
                            </button>
                            <button className="btn btn-ghost" onClick={() => setNomeando(null)}>
                              Cancelar
                            </button>
                          </span>
                        ) : (
                          <>
                            <div style={{ fontWeight: 600 }}>
                              {a.nome ?? <span className="t-cap">sem nome</span>}
                              {a.status === 'ACTIVE' && (
                                <button
                                  className="btn btn-ghost"
                                  onClick={() => {
                                    setNomeando(a.id);
                                    setNome(a.nome ?? '');
                                  }}
                                >
                                  {a.nome ? 'Renomear' : 'Dar nome'}
                                </button>
                              )}
                            </div>
                            <div className="t-cap">{a.model ?? '—'}</div>
                          </>
                        )}
                      </td>
                      <td>{a.unidade}</td>
                      <td>
                        {/* O nível é o que o hardware provou, e não o que o app afirmou. */}
                        <span
                          className={
                            a.securityLevel === 'STRONGBOX' ? 'chip chip-teal' : 'chip chip-neutral'
                          }
                        >
                          {a.securityLevel ?? 'desconhecido'}
                        </span>
                        {a.verifiedBoot && a.verifiedBoot !== 'VERIFIED' && (
                          <span className="chip chip-warn" style={{ marginLeft: 6 }}>
                            boot {a.verifiedBoot}
                          </span>
                        )}
                      </td>
                      <td>{a.lastSeenAt ? emDataHora(a.lastSeenAt) : 'nunca'}</td>
                      <td>
                        <span
                          className={
                            a.status === 'ACTIVE' ? 'chip chip-teal' : 'chip chip-danger'
                          }
                        >
                          {a.status}
                        </span>
                      </td>
                      <td>
                        {a.status === 'ACTIVE' && (
                          <button
                            className="btn btn-danger"
                            disabled={bloquear.isPending}
                            onClick={() => bloquear.mutate(a.id)}
                          >
                            Bloquear
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {bloquear.isError && <div className="aviso ruim">{String(bloquear.error)}</div>}
            {nomear.isError && <Erro erro={nomear.error} />}
          </div>

          <div className="card cd">
            <div className="cd2-t">Vincular um aparelho</div>

            {emitido ? (
              <CodigoNaTela codigo={emitido} aoFechar={() => setEmitido(null)} />
            ) : (
              <form
                className="form"
                onSubmit={(e) => {
                  e.preventDefault();
                  gerar.mutate();
                }}
              >
                <div className="campo">
                  <label htmlFor="u">Unidade</label>
                  <select id="u" value={unidade} onChange={(e) => setUnidade(e.target.value)} required>
                    <option value="">Escolha a unidade</option>
                    {unidades.data?.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.legalName}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="dica">
                  O aparelho passa a pertencer a esta unidade. Quem é o operador vem depois,
                  pelo PIN.
                </p>
                {gerar.isError && <div className="aviso ruim">{String(gerar.error)}</div>}
                <button className="btn btn-primary" type="submit" disabled={gerar.isPending || !unidade}>
                  Gerar código
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="card tbl" style={{ marginTop: 16 }}>
          <div className="cd2-t">Códigos emitidos</div>
          <p className="dica">
            O código em si não fica guardado — o banco tem só o hash dele. Esta lista responde
            se o que você ditou ainda vale.
          </p>
          {vinculos.isPending && <Carregando o="os códigos" />}
          {vinculos.isError && <Erro erro={vinculos.error} />}
          {vinculos.data?.length === 0 && <Vazio titulo="Nenhum código emitido" />}
          {vinculos.data && vinculos.data.length > 0 && (
            <table className="t">
              <thead>
                <tr>
                  <th>Unidade</th>
                  <th>Situação</th>
                  <th>Vence</th>
                  <th>Usado em</th>
                </tr>
              </thead>
              <tbody>
                {vinculos.data.map((v) => (
                  <tr key={v.id}>
                    <td>{v.unidade}</td>
                    <td>
                      <span className={`chip ${corDoVinculo(v)}`}>{situacao(v)}</span>
                    </td>
                    <td>{v.expiresAt ? emDataHora(v.expiresAt) : '—'}</td>
                    <td>{v.usedAt ? emDataHora(v.usedAt) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * O código, grande, uma vez só.
 *
 * Ele é **ditado em voz alta** — daí o tamanho, a fonte monoespaçada e o par de quatro
 * separado. O alfabeto já exclui I, O, 0 e 1 justamente porque se confundem ao telefone.
 */
function CodigoNaTela({ codigo, aoFechar }: { codigo: CodigoEmitido; aoFechar: () => void }) {
  return (
    <div>
      <p className="dica">Dite para quem está com o aparelho. Ele não aparece de novo.</p>
      <div
        style={{
          fontFamily: 'ui-monospace, monospace',
          fontSize: 34,
          fontWeight: 700,
          letterSpacing: 3,
          textAlign: 'center',
          padding: '18px 0',
        }}
      >
        {codigo.code}
      </div>
      <p className="dica">Vale até {emDataHora(codigo.expiresAt)}.</p>
      <button className="btn" type="button" onClick={aoFechar}>
        Gerar outro
      </button>
    </div>
  );
}

/**
 * Vencido é um estado que o banco não guarda — ele é derivado da hora, e por isso mora aqui.
 *
 * `CHALLENGED` conta junto com `PENDING`, e a primeira versão esquecia dele: é o código que
 * o aparelho já começou a usar e não terminou. O `expires_at` vale para os dois, então um
 * `CHALLENGED` vencido está tão morto quanto um `PENDING` vencido — e mostrá-lo sem a marca
 * mandaria a pessoa ditar de novo um código que o aparelho vai recusar, sem ninguém entender
 * por quê.
 */
function venceu(v: VinculoDeAparelho): boolean {
  const aberto = v.status === 'PENDING' || v.status === 'CHALLENGED';
  return aberto && v.expiresAt != null && new Date(v.expiresAt) < new Date();
}

function situacao(v: VinculoDeAparelho): string {
  if (venceu(v)) return 'VENCIDO';
  return v.status;
}

function corDoVinculo(v: VinculoDeAparelho): string {
  if (v.status === 'USED') return 'chip-teal';
  if (v.status === 'REVOKED' || venceu(v)) return 'chip-danger';
  return 'chip-warn';
}
